import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthProvider';
import { trackerKeys } from './hooks';
import { isTouchKind } from './leadActivity';
import { TOUCHES, touchOf } from './types';
import { leadsInStage } from './stageKeys';
import type { SupabaseClient } from '../../lib/supabase';
import type { LeadActivityKind } from './leadActivity';
import type { Lead, LeadStage, Membership, RangeReport } from './types';

/**
 * Every number the Dashboard and Reports print about outreach, computed from
 * the activity timeline. Batch 4, the last piece of the restructure.
 *
 * ── Why these numbers moved off list memberships ───────────────────────────
 *
 * Until this batch the Dashboard counted touches by reading T1–T4 dates off
 * `lead_list_memberships` — so a lead emailed outside any list counted as
 * never touched, "awaiting first touch" fired for people Nez had already
 * emailed, and the Dashboard disagreed with the Leads list about the same
 * lead. That blind spot is the complaint that started the whole restructure,
 * and the timeline exists so there is one record to count.
 *
 * Per-LIST numbers — sends per list, copy-set performance — deliberately stay
 * on memberships. A cycle is a fact about a list, and the membership row is
 * its record. The line: lead truth reads the timeline, cycle analytics read
 * the cycle.
 *
 * ── Replies are the one dual-source number, and it is stated here once ─────
 *
 * Legacy replies live only on memberships (`reply_date`): the batch-2 backfill
 * moved touches onto the timeline and deliberately invented nothing else, so
 * pre-restructure replies were never copied. New replies land on the timeline
 * as `reply_received`. Counting either source alone under-reports, so every
 * reply number in this file is the union — computed in ONE function below,
 * because two places disagreeing about what "replied" means is exactly the
 * disease this module exists to cure.
 */

/** The four columns the aggregates need. Fetching bodies for 800 rows would be waste. */
export interface ActivitySlim {
  lead_id: string;
  kind: LeadActivityKind;
  occurred_at: string;
  occurred_is_day: boolean;
  /**
   * Who logged it, or null.
   *
   * Added for the Pipeline's actions funnel, which splits the work three ways
   * — Kyu, Nez and Ced are the only people who do sales. Null is ordinary and
   * not a gap to fix: the mail sync writes rows nobody clicked for, and an
   * import has no actor at all. Those touches count toward the totals and
   * belong to no column, which is the honest place for them.
   */
  actor_id: string | null;
}

const SLIM_COLUMNS = 'lead_id, kind, occurred_at, occurred_is_day, actor_id';

/**
 * Lead rows only, for now.
 *
 * Since 20270121000100 a lead_activity row may belong to a prospect instead
 * of a lead (lead_id null, prospect_id set). Every aggregate in this module
 * is keyed by lead — reply rates, replied-lead sets, per-lead touch counts —
 * so a prospect row has no place in them yet. Counting prospecting as work
 * in the actions funnel is a later drop of the prospecting note, and it will
 * arrive by widening this type on purpose, not by a null slipping through.
 */
export async function fetchActivitySlim(client: SupabaseClient): Promise<ActivitySlim[]> {
  const { data, error } = await client
    .from('lead_activity')
    .select(SLIM_COLUMNS)
    .not('lead_id', 'is', null);
  if (error) {
    throw new Error(error.message);
  }
  const rows: ActivitySlim[] = [];
  for (const row of Array.isArray(data) ? data : []) {
    if (row.lead_id !== null) {
      rows.push({ ...row, lead_id: row.lead_id });
    }
  }
  return rows;
}

/**
 * The prospect rows, on their own.
 *
 * Kept as a second, separately typed list rather than a nullable lead_id on
 * ActivitySlim, because every aggregate above is keyed by lead and a null
 * threaded through them is exactly the silent error the lead-only fetch
 * guards against. The actions funnel is the one reader that wants both, and
 * it takes them as two arguments (pipelineActions.buildActions).
 */
export interface ProspectActivitySlim {
  prospect_id: string;
  kind: LeadActivityKind;
  occurred_at: string;
  occurred_is_day: boolean;
  actor_id: string | null;
}

const PROSPECT_SLIM_COLUMNS = 'prospect_id, kind, occurred_at, occurred_is_day, actor_id';

export async function fetchProspectActivitySlim(
  client: SupabaseClient,
): Promise<ProspectActivitySlim[]> {
  const { data, error } = await client
    .from('lead_activity')
    .select(PROSPECT_SLIM_COLUMNS)
    .not('prospect_id', 'is', null);
  if (error) {
    throw new Error(error.message);
  }
  const rows: ProspectActivitySlim[] = [];
  for (const row of Array.isArray(data) ? data : []) {
    if (row.prospect_id !== null) {
      rows.push({ ...row, prospect_id: row.prospect_id });
    }
  }
  return rows;
}

export function useAllProspectActivity() {
  const { client } = useAuth();
  return useQuery({
    queryKey: [...trackerKeys.all, 'prospects', 'activity', 'all'],
    queryFn: () => fetchProspectActivitySlim(client),
    retry: false,
  });
}

/**
 * Admin work on deals: the "Updated HubSpot" ticks (drop 4 of the prospecting
 * note). Read off deal_activity, where the trigger records each tick with
 * who and when, so no second timestamp column was needed on the deal.
 *
 * Its own list, like the prospect rows, and for a stronger reason: this must
 * NEVER be countable as outreach. Ticking a box has no natural check the way
 * a reply checks a touch, so it goes to the funnel on its own line.
 */
export interface AdminActivitySlim {
  deal_id: string;
  /** The deal's contact, so the panel can list somebody. Null on a deal with none. */
  lead_id: string | null;
  occurred_at: string;
  actor_id: string | null;
}

export async function fetchAdminActivitySlim(client: SupabaseClient): Promise<AdminActivitySlim[]> {
  const { data, error } = await client
    .from('deal_activity')
    .select('deal_id, created_at, actor_id, details, deal:deals(lead_id)')
    .eq('event_type', 'hubspot_updated_changed')
    .limit(2000);
  if (error) {
    throw new Error(error.message);
  }
  const rows: AdminActivitySlim[] = [];
  for (const row of Array.isArray(data) ? data : []) {
    // Only a tick counts. A tick taken back is a correction, not work.
    const details = row.details;
    const ticked =
      typeof details === 'object' && details !== null && !Array.isArray(details)
        ? details['to'] === true
        : false;
    if (!ticked || row.deal_id === null) continue;
    rows.push({
      deal_id: row.deal_id,
      lead_id: row.deal?.lead_id ?? null,
      occurred_at: row.created_at,
      actor_id: row.actor_id,
    });
  }
  return rows;
}

export function useAllAdminActivity() {
  const { client } = useAuth();
  return useQuery({
    queryKey: [...trackerKeys.all, 'admin-activity'] as const,
    queryFn: () => fetchAdminActivitySlim(client),
    retry: false,
  });
}

export function useAllActivity() {
  const { client } = useAuth();
  return useQuery({
    queryKey: [...trackerKeys.all, 'activity-all'] as const,
    queryFn: () => fetchActivitySlim(client),
  });
}

/**
 * The calendar day an activity belongs to, as `YYYY-MM-DD`.
 *
 * UTC for both kinds. Day-valued rows sit at midday UTC by the 20261124000100
 * convention, so their UTC date IS the recorded day. Instants get their UTC
 * date too — an aggregate that mixed "UTC for some rows, browser-local for
 * others" would double-count the evening either side of midnight.
 */
export function activityDayIso(activity: Pick<ActivitySlim, 'occurred_at'>): string {
  return activity.occurred_at.slice(0, 10);
}

export function touchesOn(activities: ActivitySlim[], dayIso: string): number {
  return activities.filter(
    (activity) => isTouchKind(activity.kind) && activityDayIso(activity) === dayIso,
  ).length;
}

export function touchesBetween(activities: ActivitySlim[], from: string, to: string): number {
  return activities.filter((activity) => {
    if (!isTouchKind(activity.kind)) {
      return false;
    }
    const day = activityDayIso(activity);
    return day >= from && day <= to;
  }).length;
}

/** Replies recorded on a day — timeline `reply_received` plus legacy membership dates. */
export function repliesOn(
  activities: ActivitySlim[],
  memberships: Membership[],
  dayIso: string,
): number {
  const fromTimeline = activities.filter(
    (activity) => activity.kind === 'reply_received' && activityDayIso(activity) === dayIso,
  ).length;
  const fromLegacy = memberships.filter((m) => m.reply_date === dayIso).length;
  return fromTimeline + fromLegacy;
}

/**
 * The leads that have ever replied. THE definition — computed here and
 * nowhere else, for the reason in the module header.
 */
export function repliedLeadIds(activities: ActivitySlim[], memberships: Membership[]): Set<string> {
  const ids = new Set<string>();
  for (const activity of activities) {
    if (activity.kind === 'reply_received') {
      ids.add(activity.lead_id);
    }
  }
  for (const membership of memberships) {
    if (membership.reply_date !== null || membership.reply_touch !== null) {
      ids.add(membership.lead_id);
    }
  }
  return ids;
}

/**
 * Reply rate: leads that replied over leads that were touched.
 *
 * Touched comes from `leads.touch_count`, the trigger-maintained counter — the
 * same number the Leads list prints, which is the point: the Dashboard and the
 * list can no longer disagree about who was touched.
 */
export function replyRateAllTime(
  leads: Lead[],
  activities: ActivitySlim[],
  memberships: Membership[],
): { replies: number; touched: number; label: string } {
  const touched = leads.filter((lead) => lead.touch_count > 0);
  const replied = repliedLeadIds(activities, memberships);
  const replies = touched.filter((lead) => replied.has(lead.id)).length;
  const label =
    touched.length === 0 ? '—' : `${String(Math.round((replies / touched.length) * 100))}%`;
  return { replies, touched: touched.length, label };
}

export interface ActivityDailyRow {
  date: string;
  newLeads: number;
  touchesSent: number;
  replies: number;
}

/** Per-day activity for a trailing window, newest first, today included. */
export function dailyRows(
  leads: Lead[],
  activities: ActivitySlim[],
  memberships: Membership[],
  todayIso: string,
  days = 14,
): ActivityDailyRow[] {
  const rows: ActivityDailyRow[] = [];
  const date = new Date(`${todayIso}T00:00:00Z`);
  for (let i = 0; i < days; i += 1) {
    const iso = date.toISOString().slice(0, 10);
    rows.push({
      date: iso,
      newLeads: leads.filter((lead) => lead.created_at.slice(0, 10) === iso).length,
      touchesSent: touchesOn(activities, iso),
      replies: repliesOn(activities, memberships, iso),
    });
    date.setUTCDate(date.getUTCDate() - 1);
  }
  return rows;
}

export interface AwaitingCounts {
  /** Inbound leads on New: they came to us and nobody has replied. The urgent number. */
  inbound: number;
  /** Every lead on New. Mostly the unworked outbound backlog. */
  all: number;
  /**
   * The same two answers as lead ids, so the banner can open them.
   *
   * Carried beside the counts rather than instead of them, and the counts are
   * taken FROM these arrays below — so the number in the sentence and the list
   * it opens cannot disagree. Before drop 141 the banner's "view them" went to
   * the Leads tab with no filter at all, landing somebody on 488 rows to find
   * 156 by eye.
   */
  inboundIds: readonly string[];
  allIds: readonly string[];
}

/**
 * Both awaiting-first-touch numbers, labelled by what they count.
 *
 * The Dashboard used to show only the inbound number while the Leads list
 * showed hundreds on New, and the gap read as a bug. It is not — they answer
 * different questions — but two unlabelled answers to what looks like one
 * question is how a screen stops being trusted, so they travel together now.
 */
export function awaitingCounts(leads: Lead[], stages: LeadStage[]): AwaitingCounts {
  const onNew = leadsInStage(leads, stages, 'new');
  const inboundIds = onNew.filter((lead) => lead.source === 'inbound').map((lead) => lead.id);
  const allIds = onNew.map((lead) => lead.id);
  // Lengths of the arrays above, never a second count of the same thing.
  return { inbound: inboundIds.length, all: allIds.length, inboundIds, allIds };
}

export interface ActivityFunnelRow {
  label: string;
  count: number;
  /** Percent of leads touched; null on the Touched row itself. */
  pctOfTouched: number | null;
}

/** The conversion funnel, with Touched and Replied read from the timeline. */
export function activityFunnel(
  leads: Lead[],
  activities: ActivitySlim[],
  memberships: Membership[],
  stages: LeadStage[],
): ActivityFunnelRow[] {
  const touched = leads.filter((lead) => lead.touch_count > 0).length;
  const replied = repliedLeadIds(activities, memberships).size;
  const inStage = (key: 'engaged' | 'converted') => leadsInStage(leads, stages, key).length;
  const pct = (count: number) => (touched === 0 ? 0 : Math.round((count / touched) * 1000) / 10);
  return [
    { label: 'Touched', count: touched, pctOfTouched: null },
    { label: 'Replied', count: replied, pctOfTouched: pct(replied) },
    { label: 'Engaged', count: inStage('engaged'), pctOfTouched: pct(inStage('engaged')) },
    {
      label: 'Converted to Deal',
      count: inStage('converted'),
      pctOfTouched: pct(inStage('converted')),
    },
  ];
}

/**
 * The exec snapshot for a date range, from the timeline.
 *
 * Same shape the Slack and email summaries already consume, so the report
 * formats did not have to know anything changed underneath them.
 */
export function rangeReportFromActivity(
  leads: Lead[],
  activities: ActivitySlim[],
  memberships: Membership[],
  stages: LeadStage[],
  from: string,
  to: string,
): RangeReport {
  const inRange = (iso: string | null) => iso !== null && iso >= from && iso <= to;
  const newInRange = leads.filter((lead) => inRange(lead.created_at.slice(0, 10)));
  const repliesInRange =
    activities.filter(
      (activity) => activity.kind === 'reply_received' && inRange(activityDayIso(activity)),
    ).length + memberships.filter((m) => inRange(m.reply_date)).length;
  return {
    from,
    to,
    newLeads: newInRange.length,
    inbound: newInRange.filter((lead) => lead.source === 'inbound').length,
    touchesSent: touchesBetween(activities, from, to),
    replies: repliesInRange,
    replyRate: replyRateAllTime(leads, activities, memberships),
    byStage: [...stages]
      .sort((a, b) => a.position - b.position)
      .map((stage) => ({
        name: String(stage.name),
        count: leads.filter((lead) => lead.stage_id === stage.id).length,
      })),
  };
}

/** Per-day rows between two dates inclusive, oldest first, bounded hard. */
export function rangeDailyFromActivity(
  leads: Lead[],
  activities: ActivitySlim[],
  memberships: Membership[],
  from: string,
  to: string,
): ActivityDailyRow[] {
  const rows: ActivityDailyRow[] = [];
  const date = new Date(`${from}T00:00:00Z`);
  for (let i = 0; i < 366; i += 1) {
    const iso = date.toISOString().slice(0, 10);
    if (iso > to) {
      break;
    }
    rows.push({
      date: iso,
      newLeads: leads.filter((lead) => lead.created_at.slice(0, 10) === iso).length,
      touchesSent: touchesOn(activities, iso),
      replies: repliesOn(activities, memberships, iso),
    });
    date.setUTCDate(date.getUTCDate() + 1);
  }
  return rows;
}

/**
 * Leads a cycle has touched, for the per-list tables that stay on memberships.
 * Exported so the one place that still counts membership touches says why.
 */
export function membershipTouchCount(membership: Membership): number {
  let count = 0;
  for (const touch of TOUCHES) {
    if (touchOf(membership, touch).sentOn !== null) {
      count += 1;
    }
  }
  return count;
}
